import { useCallback, useEffect, useRef, useState } from 'react'
import {
	activatePromo,
	checkPromo,
	clearStoredPromo,
	getStoredPromo,
	normalizePromo,
	type PromoActivation,
	type PromoPurchase,
	type PromoQuote,
} from '../api/promo'
import { getErrorMessage } from '../utils/account'
import { usePromoAvailable } from './usePromoAvailable'

export interface PromoMessage {
	reason: string
	message: string
}

interface CheckResult {
	/** Для какой покупки посчитано: сменилась покупка — расчёт устарел */
	key: string
	quote: PromoQuote | null
	error: PromoMessage | null
}

// Отказы, после которых код из ссылки ?promo= держать незачем: он не
// заработает ни на другой покупке, ни позже. Остальные (не тот тариф, мала
// сумма, «только при активации») зависят от покупки — код остаётся.
const DEAD_REASONS = new Set([
	'not_found', 'bad_format', 'inactive', 'expired', 'exhausted', 'user_limit', 'wrong_site', 'new_users_only',
])

interface Options {
	/** Пауза перед перепроверкой при смене покупки (слайдер докупки) */
	debounceMs?: number
	/** Код на бонусные токены активирован прямо из поля — освежить баланс */
	onActivated?: (res: PromoActivation) => void
}

/**
 * Состояние поля «Есть промокод?» на оплате. Применённый код перепроверяется
 * при каждой смене покупки (тип оплаты, тариф, сумма слайдера): скидка
 * считается от цены, и фиксированная может перестать подходить. quote — только
 * расчёт для ТЕКУЩЕЙ покупки; pending — расчёт ещё в пути (оплату ждём).
 * Платёж шлёт promo_code только при quote — сервер всё равно пересчитает.
 */
export function usePromoCode(purchase: PromoPurchase | null, { debounceMs = 0, onActivated }: Options = {}) {
	// Промокоды закрыты для этого юзера — поле не рисуется, код из ссылки не применяется
	const available = usePromoAvailable()
	const availableRef = useRef(available)
	availableRef.current = available
	const [open, setOpen] = useState(false)
	const [input, setInput] = useState('')
	// Код, «стоящий» на покупке, — по нему идут перепроверки
	const [applied, setApplied] = useState<string | null>(null)
	// Повторное «Применить» того же кода тоже должно перепроверить
	const [tick, setTick] = useState(0)
	const [result, setResult] = useState<CheckResult | null>(null)
	const [checking, setChecking] = useState(false)
	const [activating, setActivating] = useState(false)
	const [activated, setActivated] = useState<PromoActivation | null>(null)
	const seqRef = useRef(0)
	// Код хоть раз подошёл → при отказе после смены суммы продолжаем
	// перепроверять (вернул сумму — скидка вернулась). Неверный с первого ввода
	// код не гоняем на каждый сдвиг слайдера: неудачи копят лимит попыток.
	const wasValidRef = useRef(false)
	// «Применить» — сразу, без паузы слайдера
	const immediateRef = useRef(false)

	const purchaseKey = purchase ? JSON.stringify(purchase) : ''

	useEffect(() => {
		if (!applied || !purchaseKey) return
		const p = JSON.parse(purchaseKey) as PromoPurchase
		const delay = immediateRef.current ? 0 : debounceMs
		immediateRef.current = false
		const seq = ++seqRef.current
		const timer = window.setTimeout(async () => {
			setChecking(true)
			let next: CheckResult
			try {
				const res = await checkPromo(applied, p)
				next = res.ok
					? { key: purchaseKey, quote: res, error: null }
					: { key: purchaseKey, quote: null, error: { reason: res.reason, message: res.message } }
			} catch (e) {
				next = { key: purchaseKey, quote: null, error: { reason: 'error', message: getErrorMessage(e, 'Не удалось проверить промокод') } }
			}
			if (seq !== seqRef.current) return
			setChecking(false)
			setResult(next)
			if (next.quote) {
				wasValidRef.current = true
			} else if (!wasValidRef.current) {
				setApplied(null)
				if (next.error && DEAD_REASONS.has(next.error.reason)) clearStoredPromo(applied)
			}
		}, delay)
		return () => window.clearTimeout(timer)
	}, [applied, tick, purchaseKey, debounceMs])

	const apply = useCallback(() => {
		const code = normalizePromo(input)
		if (!code) return
		wasValidRef.current = false
		immediateRef.current = true
		setInput(code)
		setActivated(null)
		setResult(null)
		setApplied(code)
		setTick((t) => t + 1)
	}, [input])

	const remove = useCallback(() => {
		seqRef.current++
		wasValidRef.current = false
		// Юзер сам убрал код — и из ссылки он больше не подставляется
		if (applied) clearStoredPromo(applied)
		setApplied(null)
		setResult(null)
		setChecking(false)
		setInput('')
	}, [applied])

	/** Открытие оплаты: код из ссылки ?promo= — сразу в поле и на проверку. */
	const reset = useCallback(() => {
		seqRef.current++
		wasValidRef.current = false
		immediateRef.current = true
		const stored = availableRef.current ? getStoredPromo() : ''
		setOpen(Boolean(stored))
		setInput(stored)
		setApplied(stored || null)
		setResult(null)
		setChecking(false)
		setActivating(false)
		setActivated(null)
		setTick((t) => t + 1)
	}, [])

	// Код на бонусные токены, введённый на оплате (reason activation_only):
	// активируем на месте, не отправляя юзера в кабинет.
	const activate = useCallback(async () => {
		const code = normalizePromo(input || applied || '')
		if (!code || activating) return
		setActivating(true)
		try {
			const res = await activatePromo(code)
			if (res.ok) {
				seqRef.current++
				wasValidRef.current = false
				clearStoredPromo(code)
				setActivated(res)
				setApplied(null)
				setResult(null)
				setChecking(false)
				setInput('')
				onActivated?.(res)
			} else {
				setResult({ key: purchaseKey, quote: null, error: { reason: res.reason, message: res.message } })
			}
		} catch (e) {
			setResult({ key: purchaseKey, quote: null, error: { reason: 'error', message: getErrorMessage(e, 'Не удалось активировать промокод') } })
		} finally {
			setActivating(false)
		}
	}, [input, applied, activating, purchaseKey, onActivated])

	// Доступ пришёл после открытия оплаты (первый запрос) — подставить код из ссылки
	useEffect(() => {
		if (available) reset()
	}, [available, reset])

	const fresh = result && result.key === purchaseKey ? result : null
	const pending = Boolean(applied && purchaseKey) && (checking || !fresh)

	return {
		available,
		open,
		setOpen,
		input,
		setInput,
		applied,
		quote: applied && fresh ? fresh.quote : null,
		// Пока идёт перепроверка, прошлый отказ не показываем
		error: pending ? null : result?.error ?? null,
		pending,
		activating,
		activated,
		apply,
		remove,
		reset,
		activate,
	}
}

export type PromoCodeState = ReturnType<typeof usePromoCode>
